import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { ArweaveImage } from "../../components/ArweaveImage";
import { Flex } from "../../components/Grid";
import { Icon } from "../../components/Icon";
import {
  Body,
  Callout,
  Caption2,
  HeaderTitle,
  Headline,
  LargeTitle,
  Subhead,
  Title1,
  Title2,
} from "../../components/Typography";
import { Button, ButtonShapes } from "../../components/buttons/Button";
import { DropEntry, ItemType, VariantFile, VariantItem } from "../../dropTypes";
import { useAudio } from "../../hooks/media";
import { IMAGE_SIZES } from "../../lib/constants";
import {
  hideAboveMobile,
  hideOnMobile,
  largeScreenAndAbove,
  mediumScreen,
  mediumScreenAndAbove,
  mediumScreenAndBelow,
  smallScreen,
  smallScreenAndAbove,
  smallScreenAndBelow,
  xSmallScreen,
} from "../../lib/mediaQuery";
import { dropTypePrettifier } from "../../utils/strings";
import { ContentIcon } from "../drop/details/middleSection/details/content";
import { UserDetails } from "../drop/userDetails";

interface Props {
  index?: number;
  drop?: DropEntry;
  setPauseTimer?: Dispatch<SetStateAction<boolean>>;
}

export const ImageSlideOverlay = ({ drop, index, setPauseTimer }: Props) => {
  const maxNumberOfItems = 4;
  return (
    <SlideOverlay>
      <Flex
        css={`
          height: 100%;
          text-shadow: var(--text-shadow);
          max-width: 300px;
        `}
        direction={"column"}
        justify={"space-between"}
      >
        <Flex align={"center"} gap={"xs"}>
          <Title1
            css={`
              ${hideOnMobile};
              text-shadow: var(--text-shadow);
            `}
          >
            #{index + 1}
          </Title1>
          <Headline
            css={`
              ${hideAboveMobile};
              text-shadow: var(--text-shadow);
            `}
          >
            #{index + 1}
          </Headline>
          <UserDetails
            {...drop?.variants?.[0].collectionMetadata?.creators?.[0]}
          />
        </Flex>
        <ImageContainer>
          <ArweaveImage
            fill
            sizes={IMAGE_SIZES}
            alt="Vault cover image"
            src={drop?.data?.image}
            css={`
              object-fit: cover;
              border-radius: 16px;
            `}
          />
        </ImageContainer>
        <VaultNameAndType drop={drop} />
      </Flex>
      <Flex
        align={"center"}
        justify={"center"}
        css={`
          ${hideAboveMobile};
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        `}
      >
        <AudioPreviewButton
          drop={drop}
          shape={"pill"}
          setPauseTimer={setPauseTimer}
          css={`
            display: none;
            ${xSmallScreen(
              css`
                display: flex;
                opacity: 0.8;
              `
            )}
          `}
        />
      </Flex>
      <Flex
        css={`
          max-width: 40%;
          height: 100%;
        `}
        justify={"space-between"}
        align={"flex-end"}
        direction={"column"}
      >
        <Price drop={drop} />
        <Flex
          direction={"column"}
          align={"flex-end"}
          css={`
            gap: var(--spacing-xs);
          `}
        >
          {drop?.variants?.[0]?.collectionMetadata?.items
            ?.slice(0, maxNumberOfItems)
            .map((item: VariantItem) => (
              <VaultItems itemName={item.name} itemType={item.type} />
            ))}
          {drop?.variants?.[0]?.collectionMetadata?.items.length >
            maxNumberOfItems && (
            <VaultItems
              itemName={"And much more..."}
              itemType={"other" as ItemType}
            />
          )}
          <Flex gap={"xs"}>
            <AudioPreviewButton
              drop={drop}
              setPauseTimer={setPauseTimer}
              css={`
                ${xSmallScreen(hideOnMobile)}
              `}
            />
            <GetItNowButton drop={drop} />
          </Flex>
        </Flex>
      </Flex>
    </SlideOverlay>
  );
};

const SlideOverlay = styled(Flex)`
  z-index: var(--depth-after-page);
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  justify-content: space-between;
  align-items: center;
  padding: 2.5%;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 300px;
  padding-top: 100%;
  ${mediumScreenAndBelow(css`
    display: none;
  `)}
`;

interface VaultItemsProps {
  itemName: string;
  itemType: ItemType;
}

const VaultItems = ({ itemName, itemType }: VaultItemsProps) => {
  return (
    <Flex
      align={"center"}
      css={`
        gap: var(--spacing-xs);
        ${xSmallScreen(
          css`
            display: none;
          `
        )}
      `}
    >
      <Callout
        css={`
          text-shadow: var(--text-shadow);
        `}
      >
        {itemName}
      </Callout>
      <ContentIcon type={itemType} />
    </Flex>
  );
};

const VaultNameAndType = ({ drop }: DropProps) => {
  const type = `Digital ${dropTypePrettifier(
    drop?.variants?.[0]?.collectionMetadata?.type
  )}`;
  const availability = `${drop?.variants[0].itemsLoaded} available`;
  const typeAndAvailability = `${type.toUpperCase()}  ·  ${availability.toUpperCase()}`;
  return (
    <Flex direction={"column"} align={"flex-start"}>
      <Title2 css={hideOnMobile}>{drop?.data?.name}</Title2>
      <Subhead css={hideAboveMobile}>{drop?.data?.name}</Subhead>
      <Body
        css={`
          display: none;
          text-shadow: var(--text-shadow);
          ${mediumScreenAndAbove(
            css`
              display: inline-block;
            `
          )}
        `}
      >
        {typeAndAvailability}
      </Body>
      <Subhead
        css={`
          display: none;
          text-shadow: var(--text-shadow);
          ${smallScreen(
            css`
              display: inline-block;
              font-weight: 600;
            `
          )}
        `}
      >
        {typeAndAvailability}
      </Subhead>
      <Caption2
        css={`
          display: none;
          ${xSmallScreen(
            css`
              display: inline-block;
              text-shadow: var(--text-shadow);
            `
          )}
        `}
      >
        {typeAndAvailability}
      </Caption2>
    </Flex>
  );
};

interface DropProps {
  drop: DropEntry;
  className?: string;
  shape?: ButtonShapes;
  setPauseTimer?: React.Dispatch<React.SetStateAction<boolean>>;
}

const GetItNowButton = ({ drop }: DropProps) => {
  const router = useRouter();
  return (
    <>
      <Button
        variant={"primary-alt"}
        shape={"rounded"}
        onClick={() => router.push(`drop/${drop?.id}`)}
        css={`
          display: none;
          ${smallScreenAndAbove(
            css`
              display: flex;
              width: 175px;
            `
          )}
        `}
      >
        Get It Now
      </Button>
      <Button
        shape={"rounded-slim"}
        onClick={() => router.push(`drop/${drop?.id}`)}
        fontSizeXs={13}
        css={`
          display: none;
          ${xSmallScreen(
            css`
              display: flex;
              z-index: 1;
            `
          )}
        `}
      >
        Get It Now
      </Button>
    </>
  );
};

const AudioPreviewButton = ({
  drop,
  shape = "rounded",
  className,
  setPauseTimer,
}: DropProps) => {
  const audioPreviewFiles: VariantFile[] = useMemo(
    () =>
      drop?.variants?.[0].collectionMetadata?.files.filter((file) =>
        file?.preview?.mime?.startsWith("audio")
      ),
    [drop]
  );
  const previewUrls: string[] = useMemo(
    () => audioPreviewFiles.map((file) => file.preview?.uri),
    [audioPreviewFiles]
  );

  useEffect(() => {
    const stopAllOtherPlaylists = () => {
      if (previewUrls.length > 0) {
        stopPlayback();
      }
    };
    document.addEventListener("audioPlaying", stopAllOtherPlaylists);
    return () =>
      document.removeEventListener("audioPlaying", stopAllOtherPlaylists);
  }, []);

  const { state, togglePlayback, stopPlayback } = useAudio(previewUrls);

  return (
    <Flex className={className}>
      {(state === "idle" || state === "paused" || state === "ended") &&
        previewUrls.length > 0 && (
          <Button
            onClick={(event) => {
              event?.preventDefault();
              togglePlayback();
              setPauseTimer((prevIsPaused: boolean) => !prevIsPaused);
            }}
            variant={"secondary"}
            shape={shape}
            icon={<Icon name={"Play"} height={30} width={30} />}
            css={`
              width: 56px;
            `}
          />
        )}
      {state === "playing" && (
        <Button
          onClick={(event) => {
            event?.preventDefault();
            togglePlayback();
            setPauseTimer((prevIsInterrupted: boolean) => !prevIsInterrupted);
          }}
          variant={"secondary"}
          shape={shape}
          icon={<Icon name={"Pause"} height={30} width={30} />}
          css={`
            width: 56px;
          `}
        />
      )}
    </Flex>
  );
};

const Price = ({ drop }: DropProps) => {
  const price =
    Number(drop?.variants?.[0].priceUsdCents) === 0
      ? "Free"
      : `$${Number(drop?.variants?.[0].priceUsdCents) / 100}`;
  return (
    <Flex direction={"column"} align={"flex-end"}>
      <HeaderTitle
        css={`
          display: none;
          text-shadow: var(--text-shadow);
          ${largeScreenAndAbove(
            css`
              display: inline-block;
            `
          )}
        `}
      >
        {price}
      </HeaderTitle>
      <LargeTitle
        css={`
          display: none;
          text-shadow: var(--text-shadow);
          ${mediumScreen(
            css`
              display: inline-block;
            `
          )}
        `}
      >
        {price}
      </LargeTitle>
      <Title2
        css={`
          display: none;
          text-shadow: var(--text-shadow);
          ${smallScreenAndBelow(
            css`
              display: inline-block;
            `
          )}
        `}
      >
        {price}
      </Title2>
      <Callout
        css={`
          ${hideOnMobile};
          text-shadow: var(--text-shadow);
        `}
      >
        Only available on Vault
      </Callout>
      <Caption2
        css={`
          ${hideAboveMobile};
          text-shadow: var(--text-shadow);
        `}
      >
        Only available on Vault
      </Caption2>
    </Flex>
  );
};
